import { ChangeDetectionStrategy, Component, computed, inject, input, Signal } from '@angular/core';
import { ControlContainer, FormArray, FormControl, ReactiveFormsModule } from '@angular/forms';
import {
  FileSizePipe,
  getConcernTicketFileInputAccept,
  getDamageTicketFileInputAccept,
  IHierarchicalQuestionTicketOverview,
  ImmomioIconName,
  ScoreQuestionType,
  TicketIssueType,
} from '@resident-nx/shared';
import { NgTemplateOutlet } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { FileUploadValueAccessorDirective } from '../../../../../directives/form/file-upload-value-accessor.directive';
import { FileListPipe } from '../../../../../pipes/file-list.pipe';
import { DatePickerComponent } from '../../../../atoms/date-picker/date-picker.component';
import { Elevation, IconWebComponent } from '@resident-nx/web/ui';
import { TicketImageAttachmentComponent } from '../../../../molecules/ticket/ticket-image-attachment/ticket-image-attachment.component';
import { MAX_FILE_SIZE } from '../../../../../services/tickets/ticket-creation.service';
import { Store } from '@ngrx/store';
import { PdfAttachmentComponent } from '../../../../molecules/pdf-attachment/pdf-attachment.component';
import { FileUploadButtonComponent } from '../../../../atoms/file-upload-button/file-upload-button.component';
import { TicketHelperService } from '../../../../../services/tickets/ticket-helper.service';
import { CastPipe } from '../../../../../pipes/cast.pipe';
import { ImageBorderRadiusEnum } from '@resident-nx/web/util';
import { ImmomioFormFieldComponent } from '@immomio/ui/form';

@Component({
  standalone: true,
  selector: 'rs-web-ticket-detail-questions',
  templateUrl: './ticket-detail-questions.component.html',
  styleUrl: './ticket-detail-questions.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [
    {
      provide: ControlContainer,
      useFactory: () => inject(ControlContainer, { skipSelf: true }),
    },
  ],
  imports: [
    NgTemplateOutlet,
    ReactiveFormsModule,
    FileUploadValueAccessorDirective,
    IconWebComponent,
    FileListPipe,
    DatePickerComponent,
    TranslateModule,
    FileSizePipe,
    TicketImageAttachmentComponent,
    PdfAttachmentComponent,
    FileUploadButtonComponent,
    CastPipe,
    ImmomioFormFieldComponent,
  ],
})
export class TicketDetailQuestionsComponent {
  #ticketHelperService = inject(TicketHelperService);
  #store = inject(Store);
  protected readonly ScoreQuestionType = ScoreQuestionType;
  protected readonly ImmomioIconName = ImmomioIconName;
  protected readonly ImageBorderRadiusEnum = ImageBorderRadiusEnum;
  protected readonly Elevation = Elevation;
  protected readonly FormControl = FormControl;
  protected readonly File = File;
  public MAX_FILE_SIZE = MAX_FILE_SIZE;
  public questions = input.required<IHierarchicalQuestionTicketOverview[]>();
  public formArray = input.required<FormArray<FormControl | FormArray<FormControl>>>();
  public ticketType = input.required<TicketIssueType>();

  public damageTicketFileInputAccept = this.#store.selectSignal(getDamageTicketFileInputAccept);
  public concernTicketFileInputAccept = this.#store.selectSignal(getConcernTicketFileInputAccept);

  public acceptedFileTypes: Signal<string> = computed(() => {
    if (this.ticketType() === TicketIssueType.CONCERN) {
      return this.concernTicketFileInputAccept();
    }
    if (this.ticketType() === TicketIssueType.PROPERTY_DAMAGE) {
      return this.damageTicketFileInputAccept();
    }
    return '';
  });

  removeAttachment(index: number, attachmentControl: FormControl): void {
    const current: File[] = [...attachmentControl.value];
    const files = current.toSpliced(index, 1);
    // only way to create a new value instance for <input type="file" multiple>
    // needed to keep file size validation working as expected
    const fileList = this.#ticketHelperService.createFileListFromArray(files);
    // patch null when no files remain: an empty FileList is a non-array object,
    // so Validators.required treats it as present and the form stays valid.
    // null matches the value accessor's own empty contract and re-triggers required.
    attachmentControl.patchValue(files.length ? fileList : null);
  }
}
